<template>
  <div>
    <div class="zoom-actions">
      <v-btn @click="zoomIn">+</v-btn>
      <v-btn class="ml-2" @click="zoomOut">-</v-btn>
      <v-btn class="ml-2" @click="reset">
        <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
      </v-btn>
      <v-btn v-if="canTransformToV2" class="ml-2" @click="confirmTransformToV2">
        {{ $t("shortcodes.live_chat.transform_to_v2") }}
      </v-btn>
    </div>

    <!---JSON code-->
    <div class="json-action">
      <v-btn @click="collapseJsonPreview">
        <v-icon> mdi-code-json </v-icon>
      </v-btn>
    </div>

    <div v-if="displayJsonPreview" class="json-preview">
      <div class="json-preview__header">
        <div v-if="jsonFormatError" class="d-flex align-center mb-3">
          <span class="json-preview__format-error">Invalid JSON format!</span>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn v-on="on" @click="undoJson" class="ml-3">
                <v-icon>mdi-undo</v-icon>
              </v-btn>
            </template>
            <p>Undo to previous valid JSON state</p>
          </v-tooltip>
        </div>
      </div>
      <code-input
        :class="{ 'code-error': jsonFormatError }"
        :key="jsonInputKey"
        v-model="liveChatDataJsonFormat"
        :title="''"></code-input>
    </div>
    <!---JSON code END -->

    <div class="panzoom-parent">
      <div ref="panzoom">
        <!--Chatbot settings -->
        <div class="d-flex justify-center">
          <div class="chatbot-data">
            <div class="title mb-2">
              {{ $t("shortcodes.live_chat.chatbot_settings") }}
            </div>
            <v-text-field
              v-model="liveChatData.botName"
              persistent-placeholder
              :label="'Chatbot name'"
              :placeholder="'Mary'"></v-text-field>
            <VaMediaObjectFileInput
              label="Chatbot avatar"
              class="mb-4"
              :item-id="liveChatData.botAvatar.imageID"
              @update:photo="updateAvatar($event)" />

            <va-select-input
              class="mb-4"
              v-model="liveChatData.theme"
              :choices="LIVE_CHAT_THEME"
              :label="'Theme'"
              clearable></va-select-input>

            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'LiveChat (bot) message background color'"
              v-model="liveChatData.botMessageBackgroundColor"></VaColorPicker>

            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'Action items background color'"
              v-model="liveChatData.actionItemBackgroundColor"></VaColorPicker>

            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'User message background color'"
              v-model="liveChatData.userMessageBackgroundColor"></VaColorPicker>
            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'User message text color'"
              v-model="liveChatData.userMessageTextColor"></VaColorPicker>
            <div class="counter-bottom-settings">
              <div class="d-flex justify-space-between enable-counter-bottom-switch">
                <div class="counter-bottom-settings__text">Enable counter to the bottom</div>
                <v-switch
                  v-model="liveChatData.counterBottom.enabled"
                  color="primary"
                  :ripple="false"
                  hide-details
                  inset></v-switch>
              </div>
              <!-- UTM params -->
              <v-expand-transition>
                <div v-if="liveChatData.counterBottom.enabled" class="expandable-content">
                  <div class="d-flex spacing-bottom--16 spacing-top--16 counter-bottom-time-settings">
                    <va-text-input
                      class="flex-1 mr-3"
                      v-model.trim="liveChatData.counterBottom.minutes"
                      label="Minutes"
                      type="number"
                      placeholder="02"></va-text-input>
                    <va-text-input
                      class="flex-1"
                      v-model.trim="liveChatData.counterBottom.seconds"
                      type="number"
                      label="Seconds"
                      placeholder="00"></va-text-input>
                  </div>

                  <div class="spacing-bottom--16">
                    <va-text-input
                      class="flex-1"
                      v-model.trim="liveChatData.counterBottom.title"
                      label="Title on the right side"
                      placeholder="Left to claim"></va-text-input>
                  </div>
                  <div>
                    <va-text-input
                      class="flex-1"
                      v-model.trim="liveChatData.counterBottom.subtitle"
                      label="Sutitle on the right side"
                      placeholder="An available agent is reserved for this time!"></va-text-input>
                  </div>
                </div>
              </v-expand-transition>
            </div>
            <div class="d-flex justify-center">
              <v-btn :color="'primary'" class="mr-2" v-if="displayAddMessageBtn" @click="addMessage">Add message</v-btn>
            </div>
          </div>
        </div>
        <!--Chatbot settings END-->
        <!--Messages -->
        <div class="message-tree">
          <LiveChatTree :key="liveChatTreeKey" :message-tree="liveChatData.messages"></LiveChatTree>
        </div>
        <!--Messages END -->
      </div>
    </div>
  </div>
</template>

<script>
  // Components
  import LiveChatTree from "@/components/shortcodes/livechat/LiveChatTree.vue";
  import Panzoom from "@panzoom/panzoom";
  import CodeInput from "@/components/custom/CodeInput";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  // Helpers
  import debounce from "lodash.debounce";
  import cloneDeep from "lodash/cloneDeep";
  import { convertAssetsPathToAbsoluteLinks } from "@/utility/convertAssetsPaths";
  import { traverseTree } from "@/utility/traverseTree";
  import { syncTelHrefErrors } from "@/utility/validateLiveChatTelHrefs";
  import { showConfirmModal } from "@/assets/utilities/tinymce/helpers/telConfirmModal";
  // Constants
  import { MESSAGE, LIVE_CHAT_THEME } from "@/components/shortcodes/livechat/constant";
  import definedAbility from "@/ability";

  const DEFAULT_COUNTER_BOTTOM = {
    enabled: false,
    minutes: "",
    seconds: "",
    title: "",
    subtitle: "",
  };

  export default {
    name: "LiveChatDialog",
    components: { LiveChatTree, CodeInput, VaColorPicker },
    data() {
      return {
        LIVE_CHAT_THEME,
        ability: definedAbility(),
        panzoomInstance: null,
        liveChatData: {
          botName: "Mary",
          botAvatar: {
            imageID: "",
            imageURL: "",
          },
          botMessageBackgroundColor: "#f0f0f0",
          actionItemBackgroundColor: "#007bee",
          userMessageBackgroundColor: "#007bee",
          userMessageTextColor: "#FFFFFF",
          messages: [],
          animatedButton: false,
          theme: "",
          counterBottom: { ...DEFAULT_COUNTER_BOTTOM },
        },
        /**
         * When editing JSON input (code-input), user might make the JSON inside of the code-input invalid;
         * jsonFormatError is true if JSON format inside of the code-input is incorrect (not parseable using JSON.parse)
         */
        displayJsonPreview: false,
        jsonFormatError: false,
        paramData: null,
        jsonInputKey: 0,
        liveChatTreeKey: 0,
      };
    },
    computed: {
      canTransformToV2() {
        return this.$route.query.data !== "null" && this.ability.can("view", "all shortcodes");
      },
      displayAddMessageBtn() {
        return this.liveChatData.messages && this.liveChatData.messages.length === 0;
      },
      liveChatDataJsonFormat: {
        get() {
          const displayData = this.liveChatData;

          return JSON.stringify(displayData, null, " ");
        },
        set(value) {
          let parsedJson;
          try {
            this.jsonFormatError = false;
            parsedJson = JSON.parse(value);
          } catch (e) {
            this.jsonFormatError = true;
          }

          if (!this.jsonFormatError) {
            /**
             * Re-render the tree when json code input is changed (Required so that tinymce inputs update correctly);
             * Might need optimization in case of large message tree
             */
            this.rerenderMessageTree();
            // Assign separately to keep reactivity (!!!)
            this.liveChatData.messages = parsedJson.messages;
            this.liveChatData.botName = parsedJson.botName;
            this.liveChatData.botAvatar = parsedJson.botAvatar;
            this.liveChatData.botMessageBackgroundColor = parsedJson.botMessageBackgroundColor;
            this.liveChatData.actionItemBackgroundColor = parsedJson.actionItemBackgroundColor;
            this.liveChatData.userMessageBackgroundColor = parsedJson.userMessageBackgroundColor;
            this.liveChatData.animatedButton = parsedJson.animatedButton;
            this.liveChatData.theme = parsedJson.theme;

            this.liveChatData.counterBottom = { ...DEFAULT_COUNTER_BOTTOM, ...parsedJson.counterBottom };
          }
        },
      },
    },
    watch: {
      liveChatData: {
        handler(newValue) {
          this.updateValue(newValue);
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.panzoomInstance = Panzoom(this.$refs.panzoom, {
        canvas: true,
        /**
         * Ignores all panzoom events on elements with provided class;
         * This is required because sometimes we use mouse click and drag to select things (text in the input for example)
         * This prevents panzoom from moving the view when trying to select text
         */
        excludeClass: "panzoom-ignore",
        /**
         * By default, panzoom will call event.preventDefault() and event.stopPropagation() on every pan event.
         * This prevents colorpicker from being closed on outside click.
         * Override the default handleStartEvent function to solve this.
         */
        handleStartEvent: () => {},
      });

      // Set appropriate height for panzoom wrapper. Set ti to window innerheight
      this.$refs.panzoom && (this.$refs.panzoom.style.height = `${window.innerHeight - 24}px`);

      const parent = this.$refs.panzoom.parentElement;

      parent.addEventListener("wheel", event => {
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });

          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      });
    },
    methods: {
      addMessage() {
        const message = cloneDeep(MESSAGE);
        this.liveChatData.messages.push(message);
      },
      rerenderMessageTree: debounce(function () {
        this.liveChatTreeKey++;
      }, 1000),
      reset() {
        this.panzoomInstance.reset();
      },
      zoomIn() {
        this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        this.panzoomInstance.zoomOut();
      },
      confirmTransformToV2() {
        showConfirmModal({
          title: this.$t("shortcodes.live_chat.transform_to_v2"),
          message: this.$t("shortcodes.live_chat.transform_to_v2_confirm"),
          confirmLabel: this.$t("shortcodes.live_chat.transform_to_v2_confirm_button"),
        }).then(confirmed => {
          if (confirmed) {
            window.parent.postMessage({ mceAction: "transformToV2", data: this.liveChatData }, origin);
          }
        });
      },
      updateAvatar(value) {
        if (value) {
          this.liveChatData.botAvatar.imageID = value["@id"];

          if (this.ability.can("upload", "media object to inulti server")) {
            // Keep relative image URL, instead of appending inulti server URL. This way we discard inulti reference from generated pages;
            this.liveChatData.botAvatar.imageURL = value.contentUrl;
          } else {
            this.liveChatData.botAvatar.imageURL = value.contentUrl;
          }
        } else {
          this.liveChatData.botAvatar.imageID = "";
          this.liveChatData.botAvatar.imageURL = "";
        }
      },
      collapseJsonPreview() {
        this.displayJsonPreview = !this.displayJsonPreview;
      },
      /**
       * Updates the JSON input to previous valid state.
       */
      undoJson() {
        const displayData = this.liveChatData;
        this.liveChatDataJsonFormat = JSON.stringify(displayData, null, " ");
        this.jsonInputKey++;
      },
      updateValue(val) {
        window.parent.postMessage(
          {
            mceAction: "updateLiveChatData",
            data: val,
          },
          origin,
        );
      },
      syncTelHrefErrors() {
        syncTelHrefErrors(this.liveChatData.messages, this);
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (!message.data || typeof message.data !== "object") return;

          if (message.data.type === "telHrefValidationFailed") {
            this.syncTelHrefErrors();
            return;
          }

          if (message.data.type === "sendData") {
            const { data } = message.data;
            traverseTree(data.messages, message => {
              if (message.content) {
                message.content = convertAssetsPathToAbsoluteLinks(message.content);
              }

              if (message.text) {
                message.text = convertAssetsPathToAbsoluteLinks(message.text);
              }

              if (message.value) {
                message.value = convertAssetsPathToAbsoluteLinks(message.value);
              }
            });

            this.liveChatData = {
              ...data,
              counterBottom: { ...DEFAULT_COUNTER_BOTTOM, ...data.counterBottom },
            };
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";
  .zoom-actions {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
  }
  .json-action {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 102;
  }

  .title {
    color: get-color("subtitle");
    @include heading-4();
  }

  .chatbot-data {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    background: get-color("element-bg-100");
    padding: 5px 10px;
    max-width: 600px;
    min-width: 600px;
  }

  .message-tree {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    .message-item {
      border: 1px dashed get-color("primary");
      border-radius: 5px;
      background: get-color("element-bg-100");
      padding: 5px 10px;
      &__settings-wrapper {
        display: flex;
        align-items: center;
      }
      .message-item__settings {
        &--delay {
          max-width: 100px;
        }
        &--human {
          margin-left: 10px;
        }
        &--no-icon {
          margin-left: 10px;
        }
      }
    }
  }

  .json-preview {
    position: absolute;
    top: 0;
    left: 5px;
    right: 5px;
    bottom: 5px;
    z-index: 101;
    background-color: get-color("element-bg-200");
    &__header {
      display: flex;
      background: get-color("element-bg-200");
      padding: 70px 20px 0;
      &-title {
        color: get-color("subtitle");
        @include text-body-1-bold();
      }
    }
    &__format-error {
      color: get-color("error");
      @include text-body-1-bold();
    }
  }
  .json-error {
    background-color: get-color("error");
  }
  .code-error {
    border: 3px dashed get-color("error") !important;
  }

  .animated-button {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    &__title {
      color: get-color("subtitle");
      @include text-body-2-bold();
      padding-left: 4px;
    }
    &__switch {
      &::v-deep {
        .v-input--selection-controls__input {
          margin-right: 0;
        }
      }
    }
  }
  .enable-counter-bottom-switch {
    & > div {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &::v-deep {
        .v-input--selection-controls__input {
          margin-right: 0;
        }
      }
    }
  }
  .counter-bottom-settings {
    width: 100%;
    padding: 12px;
    background-color: get-color("primary-10-hover");
    border-radius: 6px;
    margin-bottom: 16px;

    &__text {
      @include text-body-2-bold();
      color: get-color("headline");
    }
  }
  .counter-bottom-time-settings {
    max-width: 230px;
  }
</style>
